/**
 * Password validation configuration and utilities using Zod
 */
import { z } from 'zod';
import validator from 'validator';

export interface PasswordRequirements {
  minLength: number;
  requireLowercase: boolean;
  requireUppercase: boolean;
  requireNumber: boolean;
  requireSpecial: boolean;
}

// Default password requirements - configurable
export const DEFAULT_PASSWORD_REQUIREMENTS: PasswordRequirements = {
  minLength: 8,
  requireLowercase: true,
  requireUppercase: true,
  requireNumber: true,
  requireSpecial: true,
};

// Shared regex pattern for special characters
const SPECIAL_CHAR_REGEX = /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/;

export interface PasswordValidationResult {
  isValid: boolean;
  errors: string[];
  strength: 'weak' | 'medium' | 'strong';
}

export interface PasswordRequirementCheck {
  met: boolean;
  label: string;
}

/**
 * Creates a Zod schema based on password requirements
 */
function createPasswordSchema(requirements: PasswordRequirements) {
  let schema = z.string().min(requirements.minLength, 
    `Password must be at least ${requirements.minLength} characters long`);

  if (requirements.requireLowercase) {
    schema = schema.regex(/[a-z]/, 'Password must contain at least one lowercase letter');
  }

  if (requirements.requireUppercase) {
    schema = schema.regex(/[A-Z]/, 'Password must contain at least one uppercase letter');
  }

  if (requirements.requireNumber) {
    schema = schema.regex(/\d/, 'Password must contain at least one number');
  }

  if (requirements.requireSpecial) {
    schema = schema.regex(SPECIAL_CHAR_REGEX, 'Password must contain at least one special character');
  }

  return schema;
}

/**
 * Validates password against configured requirements using Zod
 */
export function validatePassword(
  password: string,
  requirements: PasswordRequirements = DEFAULT_PASSWORD_REQUIREMENTS
): PasswordValidationResult {
  const schema = createPasswordSchema(requirements);
  const result = schema.safeParse(password);
  
  const errors: string[] = result.success ? [] : result.error.issues.map(e => e.message);
  const strength = calculatePasswordStrength(password, requirements);

  return {
    isValid: result.success,
    errors,
    strength,
  };
}

/**
 * Gets individual requirement checks for UI display
 */
export function getPasswordRequirementChecks(
  password: string,
  requirements: PasswordRequirements = DEFAULT_PASSWORD_REQUIREMENTS
): PasswordRequirementCheck[] {
  const checks: PasswordRequirementCheck[] = [];

  checks.push({
    met: password.length >= requirements.minLength,
    label: `At least ${requirements.minLength} characters`,
  });

  if (requirements.requireLowercase) {
    checks.push({
      met: /[a-z]/.test(password),
      label: 'One lowercase letter',
    });
  }

  if (requirements.requireUppercase) {
    checks.push({
      met: /[A-Z]/.test(password),
      label: 'One uppercase letter',
    });
  }

  if (requirements.requireNumber) {
    checks.push({
      met: /\d/.test(password),
      label: 'One number',
    });
  }

  if (requirements.requireSpecial) {
    checks.push({
      met: SPECIAL_CHAR_REGEX.test(password),
      label: 'One special character (!@#$%^&*...)',
    });
  }

  return checks;
}

/**
 * Validates email using standard validator library
 */
export function validateEmail(email: string): boolean {
  return validator.isEmail(email.trim());
}

/**
 * Calculates password strength based on character variety and length
 */
function calculatePasswordStrength(
  password: string,
  requirements: PasswordRequirements
): 'weak' | 'medium' | 'strong' {
  let score = 0;

  // Length score
  if (password.length >= requirements.minLength) score++;
  if (password.length >= requirements.minLength + 4) score++;

  // Character variety score
  if (/[a-z]/.test(password)) score++;
  if (/[A-Z]/.test(password)) score++;
  if (/\d/.test(password)) score++;
  if (SPECIAL_CHAR_REGEX.test(password)) score++;

  if (score >= 5) return 'strong';
  if (score >= 3) return 'medium';
  return 'weak';
}
